import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";

import { PerStyleVariationMapBuilder } from "@faire/retailer/features/OpenSizing/PerStyleVariationMapBuilder";
import { ILeanCartItem } from "@faire/retailer/stores/domain/CartStore/consts";
import { getCustomizationsMapFromItemCustomizations } from "@faire/retailer/services/ItemCustomization";
import { isPrePackProductOption } from "../features/PrePacks/isPrePackProduct";

export const isDiscontinued = (
  product: Pick<IProduct, "state">,
  selectedOption: undefined | IProductOption
): boolean =>
  selectedOption === undefined ||
  selectedOption.state === IProduct.State.NOT_FOR_SALE ||
  product.state === IProduct.State.NOT_FOR_SALE;

export const isProductOptionInStock = (
  productOption: IProductOption,
  perStyleOptionVariations?: PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
) => {
  if (perStyleOptionVariations) {
    return perStyleOptionVariations.some(
      (variation) => variation.inventory > 0
    );
  }

  return productOption.state === IProduct.State.FOR_SALE;
};

/**
 * Determines which purchasing option to use as the initial purchasing option
 *
 * - First tries using the token provided
 * - In general it chooses collections over options since if both exists we most likely want them to have a collection selected
 * - Then attempts to get a sane default
 */
export const getInitialProductOptionWithCustomization = (
  options: IProductOption[],
  findByToken: string,
  getAddedItemByToken: (token: string) => ILeanCartItem | undefined
): {
  initialProductOption: undefined | IProductOption;
  customizations?: Map<string, string>;
} => {
  // Get purchasingOption from cart if a token is given
  if (findByToken) {
    const cartItem = getAddedItemByToken(findByToken);
    if (cartItem) {
      const cartItemOption = options?.find(
        ({ token }) => token === cartItem.product_option_token
      );

      if (cartItemOption) {
        return {
          initialProductOption: cartItemOption,
          customizations: getCustomizationsMapFromItemCustomizations(
            cartItem.customizations || []
          ),
        };
      }
    }
  }

  // if there is a prepack collections use it
  const prePackOption = options.find((option) =>
    isPrePackProductOption(option)
  );
  if (prePackOption) {
    return {
      initialProductOption: prePackOption,
    };
  }

  // otherwise choose an option with this in mind
  //  - purchasable > non-purchasable

  const firstPurchasableOption = options.find((option) =>
    isProductOptionInStock(option)
  );
  const firstNonPurchasableOption = options.find(
    (option) => !isProductOptionInStock(option)
  );

  const initialOption = firstPurchasableOption ?? firstNonPurchasableOption;

  if (initialOption) {
    return {
      initialProductOption: initialOption,
    };
  }

  return { initialProductOption: undefined };
};
